import * as Sentry from '@sentry/gatsby';
import axios, { AxiosRequestConfig } from 'axios';
import { prepareHubspotData } from './helpers';

// strapi
export interface ICreateContactRequestStrapi {
  date: string;
  name: string;
  message: string;
  email: string;
  phone: string | null;
  nda: boolean | null;
  file: number | null;
  recaptcha_token: string;
}
export interface ISubscribeStrapi {
  date: string;
  email: string;
  recaptcha_token: string;
}
export interface ICreateFitnessAppRequestStrapi {
  name: string;
  email: string;
  phone: string | null;
  integration: string | null;
  message: string;
  date: string;
  recaptcha_token: string;
}

export interface ICreatePositionRequestStrapi {
  date: string;
  full_name: string;
  email: string;
  chosen_position: string;
  message: string | null;
  file_cv?: string | null | undefined;
}

// hubspot
export interface IHubspotContext {
  pageUri: string;
  pageName: string;
  hutk: string;
}

export interface ICreateContactRequestHubspot {
  date: string;
  firstname: string;
  message: string;
  email: string;
  phone: string | null;
  nda: boolean | null;
  // file: number | null;
}
export interface ISubscribeHubspot {
  date: string;
  email: string;
}
export interface ICreateFitnessAppRequestHubspot {
  firstname: string;
  email: string;
  phone: string | null;
  health_api_integrations: string | null;
  message: string;
  date: string;
}

const strapiAxiosInstance = axios.create({
  baseURL: process.env.GATSBY_STRAPI_URL,
});

const hubspotAxiosInstance = axios.create({
  baseURL: process.env.GATSBY_HUBSPOT_URL,
});

const strapiInstance = (props: AxiosRequestConfig) => {
  return strapiAxiosInstance(props).catch((err) => {
    Sentry.withScope(function (scope) {
      scope.setFingerprint([
        'strapi',
        `method: ${props?.method}`,
        `api url: ${props?.url}`,
        `data: ${JSON.stringify(props.data)}`,
      ]);
      Sentry.captureException(err);
    });
    throw err;
  });
};

const hubspotInstance = (props: AxiosRequestConfig) => {
  return hubspotAxiosInstance(props).catch((err) => {
    Sentry.withScope(function (scope) {
      scope.setFingerprint([
        'hubspot',
        `method: ${props?.method}` || 'method undefined',
        `formId: ${props?.url}` || 'url undefined',
        `data: ${JSON.stringify(props.data.fields)}`,
      ]);
      Sentry.captureException(err);
    });
  });
};

export const api = {
  strapi: {
    createContactRequest: (data: ICreateContactRequestStrapi) =>
      strapiInstance({
        method: 'post',
        data,
        url: '/request-contacts',
      }),
    subscribe: (data: ISubscribeStrapi) =>
      strapiInstance({
        method: 'post',
        data,
        url: '/request-subscribes',
      }),
    createFitnessAppRequest: (data: ICreateFitnessAppRequestStrapi) =>
      strapiInstance({
        method: 'post',
        data,
        url: '/request-fitness-apps',
      }),
    createFitnessAppEstimation: (data: any) =>
      strapiInstance({
        method: 'post',
        data,
        url: '/fitness-app-calculators',
      }),
    updateFitnessAppEstimation: (id: string | number, data: any) =>
      strapiInstance({
        method: 'put',
        data,
        url: `/fitness-app-calculators/${id}`,
      }),
    getFitnessAppEstimation: (id: string | number) =>
      strapiInstance({
        method: 'get',
        url: `/fitness-app-calculators/${id}`,
      }),
    createPositionRequest: (data: ICreatePositionRequestStrapi) =>
      strapiInstance({
        method: 'post',
        data,
        url: `/request-positions`,
      }),
    upload: (data: any) =>
      strapiInstance({
        method: 'post',
        data,
        url: '/upload',
      }),
  },
  hubspot: {
    createContactRequest: (data: ICreateContactRequestHubspot, context: IHubspotContext) =>
      hubspotInstance({
        method: 'post',
        data: prepareHubspotData(data, context),
        url: process.env.GATSBY_HUBSPOT_REQUEST_CONTACT_FORM_ID,
      }),
    subscribe: (data: ISubscribeHubspot, context: IHubspotContext) =>
      hubspotInstance({
        method: 'post',
        data: prepareHubspotData(data, context),
        url: process.env.GATSBY_HUBSPOT_REQUEST_SUBSCRIBE_FORM_ID,
      }),
    createFitnessAppRequest: (data: ICreateFitnessAppRequestHubspot, context: IHubspotContext) =>
      hubspotInstance({
        data: prepareHubspotData(data, context),
        method: 'POST',
        url: process.env.GATSBY_HUBSPOT_REQUEST_INTEGRATION_FORM_ID,
      }),
  },
};
